import React, { useState, useRef } from 'react'
import useSWR from 'swr'
import { uploadMatchBriefing, fetchMatchBriefings, MatchBriefingsResponse } from '../services/api'
import { Banner, Button, Pagination, Badge } from '../components'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import styles from './MatchBriefingPage.module.scss'

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-'
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const MatchBriefingPage: React.FC = () => {
    const [file, setFile] = useState<File | null>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [successMessage, setSuccessMessage] = useState<string | null>(null)
    const [isDragOver, setIsDragOver] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [daysFilter, setDaysFilter] = useState(7)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const { data, isLoading, mutate } = useSWR<MatchBriefingsResponse>(
        ['match-briefings', { days: daysFilter, page: currentPage, limit: 20 }],
        () => fetchMatchBriefings({ days: daysFilter, page: currentPage, limit: 20 }),
        { revalidateOnFocus: false, refreshInterval: 30000 }
    )

    const briefings = data?.briefings ?? []
    const pagination = data?.pagination

    const handleFileSelect = (selectedFile: File | null) => {
        if (selectedFile && selectedFile.type !== 'application/pdf') {
            setError('Only PDF files are accepted')
            return
        }
        setFile(selectedFile)
        setError(null)
        setSuccessMessage(null)
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFileSelect(e.target.files?.[0] || null)
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragOver(true)
    }

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragOver(false)
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragOver(false)
        handleFileSelect(e.dataTransfer.files?.[0] || null)
    }

    const handleRemoveFile = () => {
        setFile(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const handleUpload = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!file) {
            setError('Please select a PDF file to upload')
            return
        }

        setIsUploading(true)
        setError(null)
        setSuccessMessage(null)

        try {
            const result = await uploadMatchBriefing(file)
            setSuccessMessage(`"${result.file_name}" uploaded successfully. Briefing will be generated shortly.`)
            handleRemoveFile()
            mutate()
        } catch (err: any) {
            console.error('Upload error:', err)
            setError(err.message || 'Upload failed')
        } finally {
            setIsUploading(false)
        }
    }

    return (
        <div className={pageStyles.pageContainer}>
            <div className={pageStyles.pageHeader}>
                <h1>Match Briefings</h1>
                <p>Upload WTA/ATP pre-match notes to generate broadcast briefings.</p>
            </div>

            {successMessage && (
                <Banner
                    variant="success"
                    message={successMessage}
                    onDismiss={() => setSuccessMessage(null)}
                    autoScroll
                />
            )}

            {error && (
                <Banner
                    variant="error"
                    message={error}
                    onDismiss={() => setError(null)}
                    autoScroll
                />
            )}

            <form onSubmit={handleUpload} className={styles.uploadForm}>
                <div
                    className={`${styles.dropZone} ${isDragOver ? styles.dropZoneActive : ''} ${file ? styles.dropZoneHasFile : ''} ${error ? styles.dropZoneError : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => !file && fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf"
                        onChange={handleFileChange}
                        disabled={isUploading}
                        className={styles.hiddenInput}
                    />

                    {file ? (
                        <div className={styles.filePreview}>
                            <div className={styles.fileIcon}>PDF</div>
                            <div className={styles.fileDetails}>
                                <span className={styles.fileName}>{file.name}</span>
                                <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                            </div>
                            <button
                                type="button"
                                className={styles.removeFile}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleRemoveFile()
                                }}
                                aria-label="Remove file"
                            >
                                &times;
                            </button>
                        </div>
                    ) : (
                        <div className={styles.dropZoneContent}>
                            <div className={styles.uploadIcon}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="12" y1="18" x2="12" y2="12" />
                                    <polyline points="9 15 12 12 15 15" />
                                </svg>
                            </div>
                            <p className={styles.dropZoneText}>
                                Drag &amp; drop your PDF here, or <span className={styles.browseLink}>browse</span>
                            </p>
                            <p className={styles.dropZoneHint}>Only .pdf files are accepted</p>
                        </div>
                    )}
                </div>

                <div className={styles.formActions}>
                    <Button type="submit" disabled={isUploading || !file}>
                        {isUploading ? 'Uploading...' : 'Upload Document'}
                    </Button>
                </div>
            </form>

            {/* Recent Briefings */}
            <div style={{ marginTop: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <h2 style={{ margin: 0 }}>Recent Briefings</h2>
                    <select
                        value={daysFilter}
                        onChange={(e) => { setDaysFilter(Number(e.target.value)); setCurrentPage(1) }}
                        style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.875rem' }}
                    >
                        <option value={1}>Last 24 hours</option>
                        <option value={2}>Last 2 days</option>
                        <option value={7}>Last 7 days</option>
                        <option value={14}>Last 14 days</option>
                        <option value={30}>Last 30 days</option>
                    </select>
                </div>

                {isLoading && <p>Loading briefings...</p>}

                {!isLoading && briefings.length === 0 && (
                    <p style={{ color: '#888' }}>No briefings found in the selected time range.</p>
                )}

                {briefings.length > 0 && (
                    <>
                        <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
                            <div className={listTableStyles.cellPrimary}>Tournament</div>
                            <div className={listTableStyles.cellText}>Level</div>
                            <div className={listTableStyles.cellText}>Dates</div>
                            <div className={listTableStyles.cellText}>Uploaded</div>
                            <div className={listTableStyles.cellActions}>Actions</div>
                        </div>
                        <div className={listTableStyles.tableBody}>
                            {briefings.map((b) => (
                                <div key={b.id} className={`${listTableStyles.tableRow} ${styles.listItem}`}>
                                    <div className={listTableStyles.cellPrimary}>
                                        <strong>{b.tournament_title}</strong>
                                        {b.original_filename && (
                                            <span style={{ display: 'block', fontSize: '0.75rem', color: '#888', marginTop: '2px' }}>
                                                {b.original_filename}
                                            </span>
                                        )}
                                    </div>
                                    <div className={listTableStyles.cellText}>
                                        {b.level && <Badge variant="neutral">{b.level}</Badge>}
                                    </div>
                                    <div className={listTableStyles.cellText}>
                                        {b.tournament_dates || '-'}
                                    </div>
                                    <div className={listTableStyles.cellText}>
                                        {formatDate(b.created_at)}
                                    </div>
                                    <div className={listTableStyles.cellActions}>
                                        {b.html_s3_key && (
                                            <Button
                                                onClick={async () => {
                                                    try {
                                                        const detail = await import('../services/api').then(m => m.fetchMatchBriefing(b.id))
                                                        if (detail.html_url) {
                                                            window.open(detail.html_url, '_blank')
                                                        }
                                                    } catch {
                                                        setError('Failed to open briefing')
                                                    }
                                                }}
                                            >
                                                View
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {pagination && (
                            <Pagination pagination={pagination} onPageChange={setCurrentPage} />
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default MatchBriefingPage
